/**
 * The skeleton every modular figure is built on: where each joint sits for a camera view, pose and
 * animation phase. Body parts, clothes and weapons attach to these joints instead of carrying their
 * own coordinates, so a new pose or a new view is added here once and every outfit follows it.
 *
 * Poses come from the classic figure's `rigFor` (stride, crouch, lean, arm positions), so Model 0
 * moves exactly like the figures in the game today. Coordinates are in the figure's design space:
 * the same canvas columns as `martialArtist`, rows above `PAD_TOP`, the feet on row `GROUND`.
 */
import {
  ART_CX,
  BODY_EXTRA,
  FAR_HAND,
  FRAMES,
  LEG_EXTRA,
  LIMB,
  type MotionId,
  motionPhase,
  type Pose,
  type Rig,
  isSeated,
  motionKeys,
  rigFor,
  STANCE_COUNT,
  STANCE_PH,
  TORSO_EXTRA,
  type View,
  type View8,
  baseView,
  isLeftView,
} from '../sprites';
import { hermite, motionSpan } from '../motions/spline';
import { ARMS as HOLD_ARMS, applyHold, type HoldId } from '../motions/hold';
import { HANDS as WUSHU_HANDS, WIDTH, wushu, wushuFor } from '../motions/wushu';
import { EXTRA_HANDS } from './poseHands';
import { IDLE_KEYS } from '../motions/idles';
import {
  ELDER_WALK_HUNCH,
  RUNS,
  runPolish,
  runShift,
  walkPolish,
  walkShift,
  walkTwist,
  WALKS,
} from '../motions/walk';
import { SAVED_POSES } from './poses';
import { type Gaze, gazeSkeleton } from './gaze';

/** Joints shared by both sides of the body. */
export const MID_JOINTS = ['head', 'neck', 'chest', 'waist', 'pelvis'] as const;
/** Joints that come in pairs: `A` is the weapon side, `B` the other. */
export const LIMB_JOINTS = ['shoulder', 'elbow', 'wrist', 'hand', 'hip', 'knee', 'ankle'] as const;
export type Side = 'A' | 'B';
export type JointName = (typeof MID_JOINTS)[number] | `${(typeof LIMB_JOINTS)[number]}${Side}`;

export interface Joint {
  x: number;
  y: number;
  /** Depth toward the camera: higher is drawn later (in front). */
  z: number;
}

export interface Skeleton {
  view: View;
  /**
   * Drawn for a left-facing view (rig/dir8.ts): still facing +x as `view` is, but with the body's sides exchanged
   * in depth (and, three-quarter on, across the screen), to be mirrored once composed.
   */
  left?: boolean;
  pose: Pose;
  joints: Record<JointName, Joint>;
  /** Unit vector the toes point along on screen (forward for the turned views, down head-on). */
  toe: { x: number; y: number };
  /** Straight-on views (front or back): the figure is drawn symmetrically. */
  square: boolean;
  /** The face is turned away from the camera. */
  back: boolean;
  /** The pose's own numbers, for parts that react to them (cloth sway, hem lift). */
  rig: Rig;
  /** Animation frame within the pose's cycle (walk 0–11, idle 0–3), for secondary motion. */
  phase: number;
  /** The body's build: widths the body and every garment on it are drawn to. */
  shape: Shape;
  /** Where the hands rest this frame, when the bearing holds them somewhere other than at the sides. */
  resting: 'side' | 'front' | 'back';
  /** Head-on, the weapon arm reaches along the line of sight and shows foreshortened. */
  foreA: boolean;
  /** A tool the pose holds that its name does not say (an idle's cane; rig/tools.ts). */
  tool?: string;
  /** A gaze frame (rig/gaze.ts): where the eyes and head are turned. */
  gaze?: Gaze;
}

/** A hand-set adjustment to a pose: per joint, how far it is moved (design pixels). */
export type JointDelta = Partial<Record<JointName, readonly [number, number]>>;

/**
 * A pose set by hand in the studio's pose workshop: a built-in pose with joints moved, per camera
 * view. With `replaces`, every figure in the game strikes it in place of the built-in one.
 */
export interface PoseEdit {
  readonly id: string;
  readonly label: string;
  readonly base: Pose;
  readonly replaces: boolean;
  readonly views: Partial<Record<View, JointDelta>>;
  readonly updated: number;
}

/** The saved poses that stand in for built-in ones (the last saved wins). */
const OVERRIDES = new Map<Pose, PoseEdit>(SAVED_POSES.filter((p) => p.replaces).map((p) => [p.base, p]));

/** Bones as joint pairs, for drawing the skeleton and for parts that follow one bone. */
export const BONES: readonly (readonly [JointName, JointName])[] = [
  ['head', 'neck'],
  ['neck', 'chest'],
  ['chest', 'waist'],
  ['waist', 'pelvis'],
  ['chest', 'shoulderA'],
  ['shoulderA', 'elbowA'],
  ['elbowA', 'wristA'],
  ['wristA', 'handA'],
  ['chest', 'shoulderB'],
  ['shoulderB', 'elbowB'],
  ['elbowB', 'wristB'],
  ['wristB', 'handB'],
  ['pelvis', 'hipA'],
  ['hipA', 'kneeA'],
  ['kneeA', 'ankleA'],
  ['pelvis', 'hipB'],
  ['hipB', 'kneeB'],
  ['kneeB', 'ankleB'],
];

/** Body proportions in design pixels. The head is a little over a quarter of the standing height. */
export const BODY = {
  headR: 9,
  // Arm and leg chains are only just longer than the standing reach, so they hang almost straight
  // at rest and bend clearly only when a pose crouches or reaches. Hanging, the elbow sits at the
  // waist and the wrist at the crotch, as in a real figure.
  upperArm: 11,
  forearm: 8.5,
  /** Wrist to the middle of the palm; the hand points along the forearm. */
  hand: 2.5,
  thigh: 15.5,
  shin: 15,
} as const;

/**
 * A body's build as half-widths seen head-on — shoulders, waist, hips — plus how much thicker
 * than average its arms and legs are and how far its belly stands out in profile.
 */
export interface Shape {
  shoulder: number;
  waist: number;
  hip: number;
  limb: number;
  belly: number;
  /** Fullness of the chest (0 flat – 3 full), shown in profile and by shading head-on. */
  bust?: number;
  /** Fullness of the seat (0 flat – 3 full), shown in profile and by shading from behind. */
  seat?: number;
}

/** Steps for the bust and seat pickers. */
export const CURVES = [
  { value: 0, label: 'แบน' },
  { value: 1, label: 'เล็ก' },
  { value: 2, label: 'กลาง' },
  { value: 3, label: 'ใหญ่' },
] as const;

export const BUILDS = {
  slim: { label: 'ผอม', shape: { shoulder: 5.5, waist: 4.5, hip: 5.5, limb: -0.5, belly: 0 } },
  average: { label: 'สมส่วน', shape: { shoulder: 6, waist: 5, hip: 6, limb: 0, belly: 0 } },
  stocky: { label: 'บึกบึน', shape: { shoulder: 7.5, waist: 6, hip: 6.5, limb: 1, belly: 0 } },
  heavy: { label: 'อ้วน', shape: { shoulder: 7, waist: 7.5, hip: 7.5, limb: 1, belly: 1.5 } },
} as const satisfies Record<string, { label: string; shape: Shape }>;
export type Build = keyof typeof BUILDS;

/** The body a skeleton is built for: a build, and a woman's narrower shoulders and waist, wider hips. */
export interface BodyShape {
  build: Build;
  female?: boolean;
  age?: Age;
  height?: Height;
  /** How the figure carries itself; left out, it follows from figure and age. */
  bearing?: BearingId;
  /** How it stands at ease (ท่ายืน); left out, the plain standing pose under its bearing. */
  stance?: StanceId;
  /**
   * Stands at ease in one of its sex's stances, the one each frame's phase names (`STANCE_PH`),
   * so each person can stand their own way while sharing the look's other frames.
   */
  stanced?: boolean;
  /** A drawn blade in the right hand: a stance leaves that hand hanging at the side to hold it. */
  wielding?: boolean;
  /** The weapon is stowed across the back: drawing and sheathing send the hand over the shoulder (F2). */
  stowBack?: boolean;
  /** The hold a drawn weapon is carried in at ease (motions/hold.ts); left out, the old hanging arm. */
  hold?: HoldId;
  /** Bust and seat (0–3); left out, a woman's are small and a man's flat. */
  bust?: number;
  seat?: number;
  /**
   * Model 0 grown to adult proportions (the body below the head longer, broader shoulders, fuller
   * limbs), its head riding on the neck at its own size, the neck showing under it.
   */
  grown?: boolean;
}

/**
 * Bearing (ท่วงท่า): how a figure carries itself, laid over the shared poses rather than replacing
 * them — fighting moves stay the same for everyone; standing, walking and running change.
 */
export interface Bearing {
  /** Width of the stance (feet apart). */
  stance: number;
  /** Length and lift of each step. */
  stride: number;
  /** How far the arms swing in a stride. */
  swing: number;
  /** How much the body bobs as it moves. */
  bob: number;
  /** How far hanging hands keep from the body. */
  tuck: number;
  /** Where the hands rest while standing and walking. */
  hands: 'side' | 'front' | 'back';
}

export const BEARINGS = {
  composed: {
    label: 'สุขุม',
    bearing: { stance: 1.15, stride: 1, swing: 1, bob: 1, tuck: 1, hands: 'side' },
  },
  graceful: {
    label: 'อ่อนช้อย',
    bearing: { stance: 0.6, stride: 0.85, swing: 0.6, bob: 0.7, tuck: 0.6, hands: 'side' },
  },
  // Hands folded at the waist, over the sash: a polite, formal stance for anyone.
  reserved: {
    label: 'สำรวม',
    bearing: { stance: 0.8, stride: 0.8, swing: 0.3, bob: 0.6, tuck: 0.7, hands: 'front' },
  },
  lively: {
    label: 'ซุกซน',
    bearing: { stance: 1, stride: 0.85, swing: 1.4, bob: 1.8, tuck: 1.1, hands: 'side' },
  },
  aged: {
    label: 'ชรา',
    bearing: { stance: 0.9, stride: 0.55, swing: 0.2, bob: 0.4, tuck: 0.8, hands: 'back' },
  },
  bold: {
    label: 'ห้าว',
    bearing: { stance: 1.4, stride: 1.2, swing: 1.4, bob: 1.1, tuck: 1.3, hands: 'side' },
  },
} as const satisfies Record<string, { label: string; bearing: Bearing }>;
export type BearingId = keyof typeof BEARINGS;

/**
 * Stances (ท่ายืน): ways of standing at ease, three cut for men and three for women; each figure
 * stands in one of its own (see `appearanceOf` in sets.ts). A stance is two key poses, the breath
 * out and the breath in, looped by its motion (`MOTIONS`), and is laid over the plain standing pose:
 * how far apart the feet are, which leg takes the weight (the hips moving over it, the free hip
 * dropping and its knee easing, the shoulders tipping against the hips), a turn of the shoulders,
 * where the hands rest. Distances are design pixels as seen head-on.
 */
export interface Stance {
  readonly label: string;
  readonly female: boolean;
  /** The key poses, breathing out and in, and the motion that loops them. */
  readonly keys: readonly [Pose, Pose];
  readonly motion: MotionId;
  /** Feet apart, as `Bearing.stance`. */
  readonly feet: number;
  /** The leg that takes the weight; left out, both do. */
  readonly weight?: Side;
  /** How far the body stands over the standing leg, and further on the breath in. */
  readonly shift: number;
  readonly shiftIn: number;
  /** The free hip's drop below the standing one, and the standing side's shoulder's below the other. */
  readonly hipDrop: number;
  readonly shoulderDrop: number;
  /** The free foot set forward of the standing one (turned views). */
  readonly step: number;
  /** The shoulders turned about the spine (as `TWIST`: positive, the right shoulder forward). */
  readonly turn: number;
  /** The spine pitched (as `Rig.tilt`): negative, the chest carried back and open. */
  readonly tilt: number;
  /** How far the chest rises with the breath in. */
  readonly breath: number;
  /** How far hanging hands keep from the body, as `Bearing.tuck`. */
  readonly tuck: number;
  /** Both hands at the sides, clasped in front at the sash, or held behind the back. */
  readonly rest: 'side' | 'front' | 'back';
  /**
   * The left hand somewhere of its own: on the hip, the elbow out; on the hilt of the sword worn at
   * the left hip (or the belt there); hanging a little forward of the thigh.
   */
  readonly handB?: 'hip' | 'hilt' | 'forward';
}

export const STANCES = {
  // A grounded martial stance: feet set wide, the weight centred, the hands loose at the sides,
  // a little away from the body; the chest fills deeply with each breath.
  rooted: {
    label: 'ยืนมั่น',
    female: false,
    keys: ['standRooted', 'standRootedIn'],
    motion: 'stanceRooted',
    feet: 1.5,
    shift: 0,
    shiftIn: 0,
    hipDrop: 0,
    shoulderDrop: 0,
    step: 0,
    turn: 0,
    tilt: 0,
    breath: 0.9,
    tuck: 1.15,
    rest: 'side',
  },
  // At ease beside the blade: the weight on the right leg, the left hand resting on the hilt of the
  // sword at the left hip, the left shoulder turned a little toward it.
  sword: {
    label: 'พักมือบนด้ามกระบี่',
    female: false,
    keys: ['standSword', 'standSwordIn'],
    motion: 'stanceSword',
    feet: 1.1,
    weight: 'A',
    shift: 1.2,
    shiftIn: 0.5,
    hipDrop: 1,
    shoulderDrop: 0.6,
    step: 1.5,
    turn: -1,
    tilt: 0,
    breath: 0.6,
    tuck: 1,
    rest: 'side',
    handB: 'hilt',
  },
  // The composed master: hands clasped behind the back, the chest carried open, the weight easy
  // on the left leg.
  master: {
    label: 'ไพล่หลัง',
    female: false,
    keys: ['standMaster', 'standMasterIn'],
    motion: 'stanceMaster',
    feet: 1,
    weight: 'B',
    shift: 0.6,
    shiftIn: 0.3,
    hipDrop: 0.6,
    shoulderDrop: 0,
    step: 1,
    turn: 0,
    tilt: -3,
    breath: 0.8,
    tuck: 1,
    rest: 'back',
  },
  // Poised: weight on the right leg in a gentle contrapposto, the free knee eased in, the hands
  // lightly clasped before the sash.
  poised: {
    label: 'ยืนสง่า',
    female: true,
    keys: ['standPoised', 'standPoisedIn'],
    motion: 'stancePoised',
    feet: 0.5,
    weight: 'A',
    shift: 1.4,
    shiftIn: 0.4,
    hipDrop: 1.2,
    shoulderDrop: 0.6,
    step: 2,
    turn: 0,
    tilt: 0,
    breath: 0.5,
    tuck: 0.6,
    rest: 'front',
  },
  // A hand at the hip: the left hand set on it, the elbow out, the weight on that leg so the hip
  // carries out under the hand.
  hip: {
    label: 'มือเท้าสะเอว',
    female: true,
    keys: ['standHip', 'standHipIn'],
    motion: 'stanceHip',
    feet: 0.7,
    weight: 'B',
    shift: 1.6,
    shiftIn: 0.4,
    hipDrop: 1.2,
    shoulderDrop: 0.6,
    step: 2,
    turn: 0,
    tilt: 0,
    breath: 0.5,
    tuck: 0.7,
    rest: 'side',
    handB: 'hip',
  },
  // Turned a little: the shoulders turned from the hips, the arms relaxed, the left hand drifting
  // forward of the thigh, the weight on the right leg.
  turned: {
    label: 'เอี้ยวไหล่',
    female: true,
    keys: ['standTurned', 'standTurnedIn'],
    motion: 'stanceTurned',
    feet: 0.6,
    weight: 'A',
    shift: 1,
    shiftIn: 0.4,
    hipDrop: 0.8,
    shoulderDrop: 0.4,
    step: 1,
    turn: 2,
    tilt: 0,
    breath: 0.5,
    tuck: 0.6,
    rest: 'side',
    handB: 'forward',
  },
} as const satisfies Record<string, Stance>;
export type StanceId = keyof typeof STANCES;
export const STANCE_IDS = Object.keys(STANCES) as StanceId[];

/** The stances cut for a man's or a woman's figure. */
export const stancesFor = (female: boolean): StanceId[] =>
  STANCE_IDS.filter((id) => STANCES[id].female === female);

/** The stance a key pose belongs to, and whether it is the breath in. */
const STANCE_KEYS = new Map<Pose, { stance: Stance; inhale: boolean }>(
  STANCE_IDS.flatMap((id) => {
    const s: Stance = STANCES[id];
    return [
      [s.keys[0], { stance: s, inhale: false }],
      [s.keys[1], { stance: s, inhale: true }],
    ] as const;
  }),
);

/** The bearing a body carries unless one is chosen: from its age first, then its figure. */
export function bearingOf(b: BodyShape): Bearing {
  if (b.bearing) return BEARINGS[b.bearing].bearing;
  if (b.age === 'elder') return BEARINGS.aged.bearing;
  if (b.age === 'child' || b.age === 'teen') return BEARINGS.lively.bearing;
  return b.female ? BEARINGS.graceful.bearing : BEARINGS.composed.bearing;
}

export const AGES = {
  child: { label: 'เด็ก', scale: 0.72, width: 0.8, limb: -0.6 },
  teen: { label: 'วัยรุ่น', scale: 0.9, width: 0.92, limb: -0.3 },
  adult: { label: 'หนุ่มสาว', scale: 1, width: 1, limb: 0 },
  elder: { label: 'ผู้เฒ่า', scale: 0.96, width: 1, limb: -0.2 },
} as const;
export type Age = keyof typeof AGES;

export const HEIGHTS = {
  short: { label: 'เตี้ย', scale: 0.92 },
  average: { label: 'ปกติ', scale: 1 },
  tall: { label: 'สูง', scale: 1.08 },
} as const;
export type Height = keyof typeof HEIGHTS;

export function shapeOf(b: BodyShape): Shape {
  const s: Shape = { ...BUILDS[b.build].shape };
  const age = AGES[b.age ?? 'adult'];
  s.shoulder *= age.width;
  s.waist *= age.width;
  s.hip *= age.width;
  s.limb += age.limb;
  if (b.female) {
    s.shoulder -= 0.8;
    s.waist -= 0.7;
    s.hip += 0.7;
  }
  if (b.grown) {
    s.shoulder += b.female ? 1 : 1.8;
    s.waist += 0.4;
    s.hip += 0.6;
    s.limb += 0.6;
  }
  s.bust = b.bust ?? (b.female ? 1 : 0);
  s.seat = b.seat ?? (b.female ? 1 : 0);
  return s;
}

/**
 * Head-on, how a reach along the line of sight shows: depth turned into rows on the screen (the
 * camera looks down on the figure; the same rate a head-on stride lifts the far foot), and how
 * much of it shows out to the arm's own side.
 */
export const SEEN_DEPTH = 0.35;
export const SEEN_OUT = 0.3;

/** How far a wrist keeps from the centre of the head. */
const HEAD_CLEAR = 10.5;

/** How much farther this body's arms reach than the classic figure's. */
const REACH = 1.45;

/** Row the soles stand on. */
export const GROUND = 57 + BODY_EXTRA;

/**
 * The classic figure designs its upper body at the old short proportions and stretches the band
 * from neck to waist by `TORSO_EXTRA` rows; pose data (hand positions) is in those design rows.
 */
function stretch(y: number, o: number): number {
  const b0 = 24 + o;
  const b1 = 37 + o;
  if (y <= b0) return y;
  if (y <= b1) return b0 + ((y - b0) * (b1 - b0 + TORSO_EXTRA)) / (b1 - b0);
  return y + TORSO_EXTRA;
}

/**
 * Two-bone reach: the middle joint (elbow, knee) for a chain from `a` to `c` with bone lengths
 * `l1` and `l2`, bending toward `pref`. Out of reach, the chain points straight at the target.
 */
function bend(
  a: { x: number; y: number },
  c: { x: number; y: number },
  l1: number,
  l2: number,
  pref: { x: number; y: number },
): { x: number; y: number } {
  const dx = c.x - a.x;
  const dy = c.y - a.y;
  const d = Math.max(0.001, Math.hypot(dx, dy));
  const ux = dx / d;
  const uy = dy / d;
  if (d >= l1 + l2) return { x: a.x + ux * l1, y: a.y + uy * l1 };
  // Distance along the chain to the foot of the perpendicular, and the height of the bend.
  const along = (l1 * l1 - l2 * l2 + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, l1 * l1 - along * along));
  let nx = -uy;
  let ny = ux;
  if (nx * pref.x + ny * pref.y < 0) [nx, ny] = [-nx, -ny];
  return { x: a.x + ux * along + nx * h, y: a.y + uy * along + ny * h };
}

/** Where the weapon-side hand goes for arm poses that are not straight limbs in `LIMB`. */
const HAND_A: Partial<Record<Rig['arm'], [number, number]>> = {
  forward: [13, 26],
  guard: [10, 25],
  raise: [5, 17],
  sit: [2, 34],
  clasp: [4, 29],
};

/**
 * Hand targets tuned for one pose on the rig (classic design coordinates, as `LIMB` and
 * `FAR_HAND`), over the arm shape's shared position, so one pose can be adjusted without moving
 * every other pose (or the classic figure) that uses the same arm shape.
 */
const POSE_HANDS: Partial<Record<Pose, { A?: [number, number]; B?: [number, number] }>> = {
  // Qinggong: both arms swept well back, straight, trailing the pitched body like the sleeves.
  sprint: { A: [-12, 33], B: [-15, 33] },
  // Struck: the weapon arm flung back and up by the blow, near straight, not folded at the side.
  hurt: { A: [-14, 18] },
  // Sat leaning back: the supporting hand planted on the ground behind, not held above it.
  lounge: { B: [-8, 40] },
};

/**
 * The skeleton for a frame. `lag` is fabric sway still carried from earlier movement (the game's
 * `cloth`): it adds to the pose's own sway, so hair and ribbons keep swinging after a stop.
 */
export function skeleton(
  view8: View8,
  pose: Pose,
  ph = 0,
  lag = 0,
  body: BodyShape = { build: 'average' },
  /** The eyes shut for this frame (a blink; the face layer reads it like meditation). */
  blink = false,
  /** Joints moved by hand; by default the saved pose that replaces this one, `null` for none. */
  edit?: JointDelta | null,
  /** The weapon arm placed here instead (for an in-between frame's other end). */
  arm?: Rig['arm'],
): Skeleton {
  // A left-facing view (rig/dir8.ts) is its right-facing view with the sides exchanged (`left` below).
  const view = baseView(view8);
  const left = isLeftView(view8);
  // A figure with stances: the stance this frame's phase names (`STANCE_PH`), the phase's own part.
  if (body.stanced && !body.stance) {
    const stance = stancesFor(!!body.female)[Math.floor(ph / STANCE_PH) % STANCE_COUNT];
    return skeleton(view8, pose, ph % STANCE_PH, lag, { ...body, stance }, blink, edit, arm);
  }
  if (ph >= STANCE_PH) ph %= STANCE_PH;
  // Looking round while standing (townsfolk): the stance's own standing frame, the head turned.
  if (pose === 'glance' && body.stance) {
    const sk = skeleton(view8, 'idle', 0, lag, body, blink, edit);
    const dx = ph ? -2 : 2;
    if (!sk.square) sk.joints.head = { ...sk.joints.head, x: sk.joints.head.x + dx };
    return { ...sk, pose, phase: ph, rig: { ...sk.rig, headDx: dx } };
  }
  // A gaze frame (rig/gaze.ts): the standing or ready frame, eyes and head turned toward someone.
  if (pose === 'gaze') return gazeSkeleton(ph, (p, f) => skeleton(view8, p, f, lag, body, blink, edit, arm));
  // A motion frame: the two key poses either side of it, every joint part of the way between them.
  if (pose === 'motion') {
    // Spline blending (motions/spline.ts): the keys either side, plus the key before and after for the curve's
    // tangents, so a joint carries its speed through a key instead of stopping on it.
    const span = SPLINE ? motionSpan(ph) : null;
    const { a, b } = span ?? motionKeys(ph);
    const herm = span ? hermite(span.u) : null;
    const t = herm ? herm.s : motionKeys(ph).t;
    const A = skeleton(view8, a, 0, lag, body, blink, edit);
    const B = skeleton(view8, b, 0, lag, body, blink, edit);
    const joints = {} as Record<JointName, Joint>;
    for (const n of Object.keys(A.joints) as JointName[]) {
      const p = A.joints[n];
      const q = B.joints[n];
      joints[n] = { x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t, z: t < 0.5 ? p.z : q.z };
    }
    // Turned, joints swing round in arcs between keys far apart (a cut, a step); keys only a
    // pixel or two apart (a breath, the weight settling) blend straight, as an arc would anyway.
    const apart = (Object.keys(A.joints) as JointName[]).some(
      (n) => Math.hypot(B.joints[n].x - A.joints[n].x, B.joints[n].y - A.joints[n].y) > 3,
    );
    if (t > 0 && t < 1 && !A.square && apart) between(joints, A.joints, B.joints, t);
    if (span && herm && t > 0 && t < 1) {
      const P = span.p === a ? A : skeleton(view8, span.p, 0, lag, body, blink, edit);
      const N = span.n === b ? B : skeleton(view8, span.n, 0, lag, body, blink, edit);
      curve(joints, P.joints, A.joints, B.joints, N.joints, herm.m0, herm.m1, t);
    }
    // An arm put behind the head in one key (a hand beside or behind it) and not in the other keeps that depth only
    // while this frame's hand is still beside or behind the head; crossing in front it comes back in front (lane
    // C0-POSE-FIX: the head showed through the arm in between).
    const faceZ = A.joints.head.z - 0.15;
    for (const s of ['A', 'B'] as const) {
      const za = A.joints[`shoulder${s}`].z;
      const zb = B.joints[`shoulder${s}`].z;
      // Likewise an arm put under the torso in one key (reaching forward, seen three-quarter from behind).
      const ua = Math.abs(za + 0.2) < 1e-6;
      const ub = Math.abs(zb + 0.2) < 1e-6;
      if (ua !== ub && A.view === 'back3') {
        const z = joints[`wrist${s}`].x > joints.chest.x + 2 ? -0.2 : ua ? zb : za;
        for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const)
          joints[`${n}${s}`] = { ...joints[`${n}${s}`], z };
        continue;
      }
      const da = Math.abs(za - faceZ) < 1e-6;
      const db = Math.abs(zb - faceZ) < 1e-6;
      if (da === db) continue;
      const w = joints[`wrist${s}`];
      const beside = !A.back && ((!A.square && w.x < joints.head.x - 2) || w.y < joints.head.y - 5);
      const z = beside ? faceZ : da ? zb : za;
      for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const)
        joints[`${n}${s}`] = { ...joints[`${n}${s}`], z };
    }
    const near = t < 0.5 ? A : B;
    const mix = (x: number, y: number) => x + (y - x) * t;
    const rig: Rig = {
      ...near.rig,
      o: mix(A.rig.o, B.rig.o),
      lean: mix(A.rig.lean, B.rig.lean),
      tilt: mix(A.rig.tilt, B.rig.tilt),
      sway: mix(A.rig.sway, B.rig.sway),
      hem: mix(A.rig.hem, B.rig.hem),
      flare: mix(A.rig.flare, B.rig.flare),
      armSwing: mix(A.rig.armSwing, B.rig.armSwing),
      // The blade turns with the arm (`weaponAim` reads `armTo`).
      arm: A.rig.arm,
      ...(A.rig.arm !== B.rig.arm && t > 0 ? { armTo: B.rig.arm, armT: t, armWay: handWay(A, B) } : {}),
    };
    return { ...near, pose, phase: ph, joints, rig };
  }
  // Standing at ease in the body's own stance: a frame of the stance's loop, its two key poses
  // blended, and still a standing frame to everything that reads it (hair and hems stir as at ease).
  if (pose === 'idle' && body.stance) {
    const f = ((Math.round(ph) % FRAMES) + FRAMES) % FRAMES;
    const sk = skeleton(view8, 'motion', motionPhase(STANCES[body.stance].motion, f), lag, body, blink, edit);
    return { ...sk, pose, phase: ph, rig: { ...sk.rig, sway: rigFor('idle', ph).sway + lag } };
  }
  const shape = shapeOf(body);
  const rig = rigFor(pose, ph);
  if (arm) {
    rig.arm = arm;
    delete rig.armTo;
  }
  // F2: a weapon worn on the back is drawn from over the shoulder, not from the hip.
  if (body.stowBack && (pose === 'sheathe' || pose === 'unsheathe' || pose === 'walkSheathe' || pose === 'walkUnsheathe')) {
    if (!arm && rig.arm === 'hip') rig.arm = 'overBack';
    if (rig.armTo === 'hip') rig.armTo = 'overBack';
  }
  rig.sway += lag;
  // A stance's key pose: its feet, its lean and its left hand (see `STANCES`).
  const idleKey = IDLE_KEYS.get(pose);
  const stand = STANCE_KEYS.get(pose) ?? idleKey;
  // A drawn weapon at ease: the figure's hold laid over the hanging arms (motions/hold.ts); the off hand only when a
  // stance has not put it on the hip or the hilt.
  if (!arm) applyHold(pose, rig, !!stand, body.hold, !!stand?.stance.handB);
  const st = stand?.stance;
  /** The other leg from the one taking the weight. */
  const free: Side | undefined = st?.weight && (st.weight === 'A' ? 'B' : 'A');
  if (st) {
    rig.tilt = st.tilt;
    if (st.handB === 'hip') rig.far = 'hip';
    // The free foot a step ahead of the standing one, which draws back a little (turned views).
    rig.f1 = free === 'B' ? st.step : -1;
    rig.f2 = free === 'A' ? st.step : free ? -1 : 1;
    // An idle that places its own feet (a kick, a tapping toe; motions/idles.ts).
    if (idleKey?.feet) [rig.f1, rig.f2] = idleKey.feet;
  }
  const bearing = bearingOf(body);
  // The bearing scales how the body moves when it is only standing, walking or running.
  // (The stride's two steps are walk frames, for stopping and landing into a run.)
  const carriage =
    pose === 'idle' ||
    pose === 'walk' ||
    pose === 'sprint' ||
    pose === 'haul' ||
    pose === 'stepA' ||
    pose === 'stepB' ||
    pose === 'walkSheathe' ||
    pose === 'walkUnsheathe';
  if (carriage) {
    rig.f1 *= bearing.stride;
    rig.f2 *= bearing.stride;
    rig.l1 *= bearing.stride;
    rig.l2 *= bearing.stride;
    rig.armSwing *= bearing.swing;
    rig.o *= bearing.bob;
  }
  // The walk's review round (motions/walk.ts), on once the walk is approved (or forced on in the studio): the hips
  // ride over the standing foot, the shoulders turn against the stride, an elder walks with the back rounded.
  const walkNew = WALKS.has(pose) && wushu();
  if (walkNew && body.age === 'elder' && rig.hunch === undefined) rig.hunch = ELDER_WALK_HUNCH;
  if (walkNew) walkPolish(rig, body, ph, bearing.stride);
  // The run's review round (motions/walk.ts), on once the qinggong run (`pose:sprint`) is approved.
  const runNew = RUNS.has(pose) && wushuFor('sprint');
  if (runNew) runPolish(rig, body);
  const stance = st
    ? st.feet
    : carriage || pose === 'ready'
      ? bearing.stance
      : (wushuFor(pose) && WIDTH[pose]) || 1;
  /** How far hanging hands keep from the body. */
  const tuck = st ? st.tuck : bearing.tuck;
  const square = view === 'front' || view === 'back';
  const back = view === 'back' || view === 'back3';
  const side = view === 'side';
  const sit = isSeated(pose);
  const cx = ART_CX;
  // Longer legs take longer strides (as in the classic figure); head-on there is no stride at all.
  const LS = 1 + LEG_EXTRA / 27;
  // The new walk's feet are kept smooth (motions/walk.ts): rounded, the planted foot slid 2 px, 3 px, 2 px … per frame.
  const f1 = square ? 0 : walkNew ? rig.f1 * LS : Math.round(rig.f1 * LS);
  const f2 = square ? 0 : walkNew ? rig.f2 * LS : Math.round(rig.f2 * LS);
  const l1 = Math.round(rig.l1 * LS);
  const l2 = Math.round(rig.l2 * LS);
  const lean = square ? 0 : rig.lean;
  const headDx = square ? 0 : rig.headDx;
  const o = rig.o + (sit ? LEG_EXTRA : 0);
  const armX = square ? 2 : 0;
  /** Upper-body point from classic design coordinates. */
  const up = (x: number, y: number, z: number): Joint => ({ x: cx + x + lean + lx, y: stretch(y + o, o), z });

  // Side A is the figure's right (the weapon hand), B its left. Facing the camera (front, and
  // front3 turned toward screen-right) the right side is on the screen's left, as with a person
  // facing you; facing away (back, back3) it is on the screen's right; in profile both sides
  // overlap. `lat` is the screen side the right shoulder, hip and foot are on.
  // Facing left (rig/dir8.ts), three-quarter on, the right side lies on the other side of the screen than it does
  // facing right (it is the far side now); in profile only its depth changes.
  const lat = (view === 'front' || view === 'front3' ? -1 : 1) * (left && !side ? -1 : 1);
  /**
   * How much of the body's width, left to right, shows across the screen: all of it head-on, about
   * three quarters turned, almost none in profile, where the two sides stack one behind the other
   * and only a stride separates the legs.
   */
  const wide = square ? 1 : side ? 0.15 : 0.75;
  // Depth: the right side faces the camera in the turned views; head-on both arms hang beside the body.
  const zA = square ? 0.8 : left ? -1 : 1;
  const zB = square ? 0.8 : left ? 1 : -1;
  /**
   * Half the shoulder span: widest head-on, narrowest in profile. The joint sits just outside the
   * torso's edge, so the arms hang beside the body (the shoulders as wide as the head) instead of
   * eating into it.
   */
  const sh = square ? shape.shoulder + 1 : side ? 1 : shape.shoulder * 0.83;
  /** Which way a side of the body lies across the screen, scaled by how much of the width shows. */
  const across = (s: Side) => (s === 'A' ? lat : -lat) * wide;
  /** A stance's weight: the hips and everything above them stand over the standing leg. */
  const lx = st?.weight
    ? across(st.weight) * (st.shift + (stand?.inhale ? st.shiftIn : 0))
    : walkNew
      ? across('A') * walkShift(rig, body)
      : runNew
        ? across('A') * runShift(rig, body)
        : 0;
  /** How much of a tilt across the body (one hip or shoulder higher) shows: least in profile. */
  const tipSeen = square ? 1 : side ? 0.3 : 0.8;

  const head = { x: cx + 1 + lean + headDx + lx, y: 14 + o, z: 0.6 };
  const neck = up(0, 23, 0.1);
  /** Shoulders sit a row below the classic figure's, so a little neck shows under the chin. */
  const drop = (j: Joint): Joint => ({ ...j, y: j.y + 1 });
  const chest = drop(up(0, 26, 0));
  const waist = up(0, 37, 0);
  const pelvis = { x: cx + lean + lx, y: 48 + o, z: 0 };
  // The shoulder joints sit a few rows under the top of the torso, below the slope from the neck,
  // so the line from the neck runs down over the top of the arm instead of the arm shrugging up.
  const point = (j: Joint): Joint => ({ ...j, y: j.y + (square ? 3 : 2) });
  const shoulderA = point(drop(up(lat * sh, 26, zA)));
  const shoulderB = point(drop(up(-lat * sh, 26, zB)));
  // Against the hips, the shoulder over the standing leg drops (contrapposto).
  const tip = { A: 0, B: 0 };
  if (st?.weight && free) {
    tip[st.weight] = (st.shoulderDrop * tipSeen) / 2;
    tip[free] = -tip[st.weight];
    shoulderA.y += tip.A;
    shoulderB.y += tip.B;
  }
  /**
   * Where the classic figure put each shoulder (A always on the right): pose reaches start there.
   * Every view measures the reach as in profile (the shoulders a pixel either side of the spine),
   * so a cut or thrust carries as far three-quarter on as side on instead of stopping short in
   * front of the chest, and head-on the arm is the profile's arm mirrored out to its side, matching
   * the blade (`weaponAim` mirrors the profile's angle) instead of buckling at the elbow.
   */
  const classicA = drop(up(1, 26, zA));
  const classicB = drop(up(-1, 26, zB));
  /** Hanging hands reach the crotch, lower than the classic figure's short arms. */
  const hang = (j: Joint): Joint => ({ ...j, y: j.y + 4 });
  /**
   * The classic poses place hands for short arms; this body's arms are about half as long again,
   * so a reach is stretched out from the shoulder in the same direction (a thrust straightens the
   * arm instead of bending it like a punch).
   */
  const reach = (sh: Joint, from: Joint, j: Joint, s: Side): Joint => {
    // Head-on, sideways reaches were drawn for a right arm on the screen's right; with it on the
    // left (facing the camera) they point the other way, so arms open out instead of crossing —
    // except a hand held before the chest (a salute, the blade presented upright), which comes in
    // to the middle.
    const before = s === 'A' && (rig.arm === 'chest' || rig.arm === 'clasp');
    const dx = (j.x - from.x) * REACH * (square ? (before ? -lat : lat) : 1);
    const dy = (j.y - from.y) * REACH;
    // Never past the arm's own length: an overhead cut points straight up rather than stretching.
    const k = Math.min(1, ((BODY.upperArm + BODY.forearm) * 0.98) / Math.max(0.001, Math.hypot(dx, dy)));
    let x = sh.x + dx * k;
    // The classic reaches mix forward with sideways; head-on, forward is toward the camera, so a
    // hand reaching in front of the body comes to the middle (where two hands meet on a handle)
    // and no farther, instead of swinging right across to the other side.
    if (square) {
      const out = s === 'A' ? lat : -lat;
      x = cx + out * Math.max(out * (x - cx), -1);
    }
    return { x, y: sh.y + dy * k, z: j.z };
  };

  // ── hands: the pose places the wrist; the palm continues along the forearm ──
  /**
   * A hanging arm swinging forward bends at the elbow, the forearm coming up (motions-polish #4: the walk's arms
   * straightened on the forward swing); swinging back it stays long. port off: no bend.
   */
  const fore = (swing: number) => (wushu() && !square ? Math.max(0, swing) * 0.6 : 0);
  const lim = LIMB[rig.arm];
  const posed = (wushuFor(pose) ? WUSHU_HANDS[pose] : undefined) ?? idleKey?.hands ?? POSE_HANDS[pose] ?? EXTRA_HANDS[pose] ?? {};
  let wristA: Joint =
    rig.arm === 'hang'
      ? hang({
          ...up(0, 37 + tip.A - fore(rig.armSwing), zA),
          x: shoulderA.x + lat * (3 + armX) * wide * tuck + rig.armSwing,
        })
      : reach(
          shoulderA,
          classicA,
          posed.A
            ? up(...posed.A, zA)
            : lim
              ? up(lim.hand[0] + armX + (rig.arm in HOLD_ARMS ? rig.armSwing : 0), lim.hand[1], zA)
              : up(...(HAND_A[rig.arm] ?? [7, 37]), zA),
          'A',
        );
  /**
   * Head-on, the weapon arm's forward reach runs along the line of sight: the arm is solved as in
   * profile (shoulder, elbow, wrist in the plane it swings in) and each joint seen down the camera,
   * forward turned into depth — toward the camera reads lower on the screen, away higher — with a
   * little of it out to the arm's own side so it clears the body. Its bones keep their length in
   * depth and show foreshortened, as the blade does (`weaponAim`). Arms that open sideways (out for
   * balance), rest on the body or hold before the chest keep their head-on placing.
   */
  const foreA =
    square &&
    !['hang', 'out', 'chest', 'clasp', 'sit', 'hip', 'fold', 'chin', 'face', 'clap', 'clapOpen', 'overBack'].includes(
      rig.arm,
    ) &&
    pose !== 'drink' &&
    pose !== 'call';
  let fixedElbowA: Joint | null = null;
  if (foreA) {
    const t = posed.A ?? lim?.hand ?? HAND_A[rig.arm] ?? [7, 37];
    const to = up(t[0] + (posed.A ? 0 : lim ? armX : 0), t[1], zA);
    let px = (to.x - classicA.x) * REACH;
    let py = (to.y - classicA.y) * REACH;
    const k = Math.min(1, ((BODY.upperArm + BODY.forearm) * 0.98) / Math.max(0.001, Math.hypot(px, py)));
    px *= k;
    py *= k;
    const el = bend({ x: 0, y: 0 }, { x: px, y: py }, BODY.upperArm, BODY.forearm, { x: -py, y: px });
    const depth = view === 'front' ? 1 : -1;
    const seen = (q: { x: number; y: number }, z: number): Joint => ({
      x: shoulderA.x + lat * Math.abs(q.x) * SEEN_OUT,
      y: shoulderA.y + q.y + q.x * SEEN_DEPTH * depth,
      z,
    });
    wristA = seen({ x: px, y: py }, zA);
    fixedElbowA = seen(el, shoulderA.z);
  }
  let wristB: Joint =
    rig.far === 'hang'
      ? hang({
          ...up(0, 37 + tip.B - fore(-rig.armSwing), zB),
          x: shoulderB.x - lat * (2 + armX) * wide * tuck - rig.armSwing,
        })
      : reach(shoulderB, classicB, up(...(posed.B ?? FAR_HAND[rig.far]), zB), 'B');
  // Elbows bend down and back in the turned views, outward head-on. A hanging arm seen head-on
  // bends (if at all) toward the camera, so its elbow stays on the line from shoulder to hand.
  const elbow = (sh: Joint, hand: Joint, s: Side, hanging: boolean): Joint => {
    if (square && hanging) {
      const t = BODY.upperArm / (BODY.upperArm + BODY.forearm);
      return { x: sh.x + (hand.x - sh.x) * t, y: sh.y + (hand.y - sh.y) * t, z: sh.z };
    }
    // Hands on the hips: the elbows stick out to the sides (back, seen turned).
    const akimbo = (s === 'A' ? rig.arm : rig.far) === 'hip';
    /** This arm's own side of the screen head-on, where its elbow bows out. */
    const out = s === 'A' ? lat : -lat;
    // Turned, the forearm folds toward the front of the upper arm, so the elbow sits behind the
    // line from shoulder to hand as the arm itself would see it: back for a hanging arm, below
    // for one reaching forward, in front for one raised overhead or cocked behind the head.
    const ux = hand.x - sh.x;
    const uy = hand.y - sh.y;
    const pref = akimbo
      ? square
        ? { x: out, y: 0.1 }
        : { x: -1, y: 0.2 }
      : square
        ? { x: out, y: 0.4 }
        : { x: -uy, y: ux };
    return { ...bend(sh, hand, BODY.upperArm, BODY.forearm, pref), z: sh.z };
  };
  /**
   * Keeps a wrist where the arm can put it: no farther from the shoulder than the arm is long
   * (a swinging hang could overshoot), and never inside the head (a raised casting hand goes
   * beside the face, not through it).
   */
  const reachable = (sh: Joint, w: Joint, onFace = false): Joint => {
    let { x, y } = w;
    const hd = Math.hypot(x - head.x, y - head.y);
    if (hd < HEAD_CLEAR && !onFace) {
      const ux = hd > 0.001 ? (x - head.x) / hd : 1;
      const uy = hd > 0.001 ? (y - head.y) / hd : 0;
      x = head.x + ux * HEAD_CLEAR;
      y = head.y + uy * HEAD_CLEAR;
    }
    const d = Math.hypot(x - sh.x, y - sh.y);
    const max = (BODY.upperArm + BODY.forearm) * 0.98;
    if (d > max) {
      x = sh.x + ((x - sh.x) / d) * max;
      y = sh.y + ((y - sh.y) / d) * max;
    }
    return { x, y, z: w.z };
  };
  // Resting hands, while the arms would otherwise hang: clasped in front, or held behind the back.
  const resting = st
    ? st.rest
    : carriage && rig.arm === 'hang' && rig.far === 'hang'
      ? bearing.hands
      : 'side';
  // In a stance a drawn blade stays in the hand hanging at the side; the other hand rests alone.
  const restsA = resting !== 'side' && !(st && body.wielding);
  if (resting !== 'side') {
    const ahead = square ? 0 : resting === 'front' ? 3 : -4;
    // Folded hands rest at the waist, over the sash (a stance's a little lower); hands behind the
    // back sit just above the seat.
    const y = waist.y + (resting === 'front' ? (st ? 1 : -2) : 1);
    if (restsA)
      wristA = { x: waist.x + ahead + (square ? -lat : 0), y, z: resting === 'back' ? -0.9 : wristA.z };
    wristB = {
      x: waist.x + ahead + (square && restsA ? lat : 0),
      y: y + 0.5,
      z: resting === 'back' ? -0.9 : wristB.z,
    };
  }
  // Arms placed against the body (emotes): on the hips, folded, at the chin or over the face, clapping.
  const placed = (arm: string, s: Side): Joint | null => {
    const shoulder = s === 'A' ? shoulderA : shoulderB;
    /** This arm's side of the screen head-on; forward on the screen in the turned views. */
    const out = s === 'A' ? lat : -lat;
    const fwd = square ? 0 : 1;
    /** In front of the body: behind it, as the camera sees it, from the back. */
    const front = back ? -0.9 : 0.7;
    const near = s === 'A' ? 0.02 : 0;
    switch (arm) {
      case 'hip':
        // Knuckles on the hip bone, a little in from the shoulder, so the elbow bows out.
        return { x: shoulder.x - out * (square ? 1 : 0.5) - fwd, y: waist.y + 1, z: shoulder.z };
      case 'overBack':
        // The hand up over the rear shoulder, at the hilt standing past it.
        return { x: shoulder.x - fwd * 3 - (square ? out * 0.5 : 0), y: shoulder.y - 5, z: back ? 0.7 : -0.9 };
      case 'fold':
        return {
          x: chest.x + fwd * 8 - out * (square ? 2 : 1),
          y: chest.y + (s === 'A' ? 5 : 6),
          z: front + near,
        };
      case 'chin':
        return { x: head.x + fwd * 3 + (square ? out : 0), y: head.y + 8, z: back ? -0.9 : head.z + 0.1 };
      case 'face':
        return {
          x: head.x + fwd * 3 + (square ? out * 2 : s === 'A' ? 0 : -1),
          y: head.y + (s === 'A' ? 3 : 4),
          z: back ? -0.9 : head.z + 0.1 + near,
        };
      case 'clap':
        // Hands meet a forearm's length in front of the chest, the elbows bent about square.
        return { x: chest.x + fwd * 11 + (square ? out * 0.5 : 0), y: chest.y + 5, z: front + near };
      case 'clapOpen':
        return {
          x: chest.x + fwd * (s === 'A' ? 12 : 9) + (square ? out * 4 : 0),
          y: chest.y + (s === 'A' ? 4 : 6),
          z: front + near,
        };
      default:
        return null;
    }
  };
  wristA = placed(rig.arm, 'A') ?? wristA;
  // A cup at the lips, or a hand cupped at the mouth to call out (rather than raised overhead as
  // in a wave): just in front of the mouth, in the middle head-on.
  const mouth = pose === 'drink' || pose === 'call';
  if (mouth)
    wristA = {
      x: head.x + (square ? -lat : 5),
      y: head.y + (square ? 7 : 5),
      z: back ? -0.9 : head.z + 0.1,
    };
  wristB = placed(rig.far, 'B') ?? wristB;
  if (st?.handB === 'hilt') {
    // On the hilt of the sword worn at the left hip (`stowAt`: the grip at the belt, forward of the
    // hip), the elbow easing out; head-on, at that hip.
    const hipX = cx - lat * shape.hip * 0.5 * wide + lean + lx;
    // Turned, the far hand is round the front of the body, only its edge showing past it.
    wristB = { x: hipX + (square ? -lat : 2), y: pelvis.y - 5, z: wristB.z };
    // Both hands on it (an idle resting its hands in front, C0-POSE-FIX fHilt): the left pushes the scabbard down at
    // the mouth, the right grips the hilt up and forward of it along the sword's line, so the two hands read apart
    // on the sword instead of clasped at the belly.
    if (resting === 'front' && restsA) {
      wristB = { ...wristB, y: wristB.y + 1 };
      wristA = { x: wristB.x + (square ? lat * 3 : 4), y: wristB.y - 5, z: wristA.z };
    }
  } else if (st?.handB === 'forward' && rig.far === 'hang')
    // Drifting a little forward of the thigh; head-on, in over it.
    wristB = { ...wristB, x: wristB.x + (square ? lat : 2) };
  /** Hands that belong in front of the face (a hand to the chin, hands over the eyes). */
  const atFace = { A: rig.arm === 'chin' || rig.arm === 'face' || mouth, B: rig.far === 'face' };
  // Hands meant for the face are drawn in front of it, so they keep their place on it.
  const solvedA = wristA;
  wristA = reachable(shoulderA, wristA, atFace.A);
  // Kept off the face, the foreshortened arm's elbow follows its wrist halfway.
  if (fixedElbowA) {
    fixedElbowA.x += (wristA.x - solvedA.x) * 0.5;
    fixedElbowA.y += (wristA.y - solvedA.y) * 0.5;
  }
  wristB = reachable(shoulderB, wristB, atFace.B);
  const elbowA = fixedElbowA ?? elbow(shoulderA, wristA, 'A', rig.arm === 'hang');
  const elbowB = elbow(shoulderB, wristB, 'B', rig.far === 'hang');
  // Hands resting before or behind the body: the upper arms hang beside it, the elbows at the
  // waist (turned, a little forward for hands in front, back for hands behind), and only the
  // forearms come in to the hands, instead of the whole arm slanting in across the body.
  if (resting !== 'side')
    for (const [s, sh, el] of [
      ['A', shoulderA, elbowA],
      ['B', shoulderB, elbowB],
    ] as const)
      if (s === 'B' || restsA) {
        el.x = sh.x + (square ? (s === 'A' ? lat : -lat) * -0.5 : resting === 'back' ? -2.5 : 1.5);
        el.y = sh.y + BODY.upperArm * 0.95;
      }
  const palm = (el: Joint, wr: Joint): Joint => {
    const d = Math.max(0.001, Math.hypot(wr.x - el.x, wr.y - el.y));
    return { x: wr.x + ((wr.x - el.x) / d) * BODY.hand, y: wr.y + ((wr.y - el.y) / d) * BODY.hand, z: wr.z };
  };
  const handA = palm(elbowA, wristA);
  const handB = palm(elbowB, wristB);

  // ── legs ──
  const hipA = {
    x: cx + lat * shape.hip * 0.5 * wide + lean + lx,
    y: pelvis.y,
    z: square ? -0.4 : left ? -0.5 : -0.3,
  };
  const hipB = {
    x: cx - lat * shape.hip * 0.5 * wide + lean + lx,
    y: pelvis.y,
    z: square ? -0.4 : left ? -0.3 : -0.5,
  };
  // A stance's free hip drops below the standing one.
  if (st?.weight && free) {
    const d = (st.hipDrop * tipSeen) / 2;
    (st.weight === 'A' ? hipA : hipB).y -= d;
    (free === 'A' ? hipA : hipB).y += d;
  }
  let ankleA: Joint;
  let ankleB: Joint;
  let kneeA: Joint;
  let kneeB: Joint;
  if (sit) {
    // Cross-legged: knees out to the sides, ankles tucked in front of the hips.
    kneeA = { x: cx + 11, y: GROUND - 3, z: hipA.z };
    kneeB = { x: cx - 9, y: GROUND - 3, z: hipB.z };
    ankleA = { x: cx - 1, y: GROUND - 2, z: 0.2 };
    ankleB = { x: cx + 4, y: GROUND - 2, z: 0.15 };
  } else {
    // Strides step along the facing (+x); each foot keeps to its own side.
    ankleA = { x: cx - 0.5 + lat * shape.hip * 0.58 * wide * stance + f2, y: GROUND - 3 - l2, z: hipA.z };
    ankleB = { x: cx - 0.5 - lat * shape.hip * 0.58 * wide * stance + f1, y: GROUND - 3 - l1, z: hipB.z };
    if (square) {
      // Feet straight under their own hips (the right foot on the right hip's side).
      ankleA.x = cx + lat * shape.hip * 0.5 * stance;
      ankleB.x = cx - lat * shape.hip * 0.5 * stance;
      // Head-on a stride runs toward and away from the camera: the foot farther away shows higher
      // up the screen, and the nearer leg is drawn over the other.
      const away = view === 'front' ? -1 : 1;
      const fa = Math.round(rig.f2 * LS);
      const fb = Math.round(rig.f1 * LS);
      // Only a stride shows much of this; a planted stance would look like a lifted foot, so it
      // shifts a couple of pixels at most.
      const rise = (f: number) => Math.min(carriage && pose !== 'idle' ? 8 : 2, Math.max(0, away * f) * 0.35);
      ankleA.y -= Math.round(rise(fa));
      ankleB.y -= Math.round(rise(fb));
      const near = Math.sign(fa - fb) * -away * 0.02;
      hipA.z += near;
      hipB.z -= near;
      ankleA.z = hipA.z;
      ankleB.z = hipB.z;
    }
    // Knees bend forward in the turned views; head-on they bend toward the camera, which only
    // shortens the leg on screen, so the knee stays on the straight line from hip to ankle.
    const knee = (hip: Joint, ankle: Joint, out: number): Joint => {
      if (!square) {
        // A foot planted beyond the straight leg's reach (the back foot of a long lunge) rises onto
        // its toes, the heel lifting, instead of the shin stretching to reach it (in a walk, the
        // push-off foot's toe-off).
        const L = BODY.thigh + BODY.shin;
        const dx = ankle.x - hip.x;
        if (Math.hypot(dx, ankle.y - hip.y) > L + 0.6 && Math.abs(dx) < L)
          ankle.y = hip.y + Math.sqrt(L * L - dx * dx);
        return { ...bend(hip, ankle, BODY.thigh, BODY.shin, { x: 1, y: 0 }), z: hip.z };
      }
      const t = BODY.thigh / (BODY.thigh + BODY.shin);
      // A knee bent toward the camera only shortens the leg on screen; bent hard (a crouch, a
      // leap) it also splays out a little, so the bend reads instead of the leg looking stunted.
      const slack = Math.max(0, BODY.thigh + BODY.shin - Math.hypot(ankle.x - hip.x, ankle.y - hip.y) - 1.5);
      return {
        x: hip.x + (ankle.x - hip.x) * t + out * slack * 0.45,
        y: hip.y + (ankle.y - hip.y) * t,
        z: hip.z,
      };
    };
    kneeA = knee(hipA, ankleA, lat);
    kneeB = knee(hipB, ankleB, -lat);
    // Head-on, a stance's free knee eases in toward the other.
    if (free && square) {
      if (free === 'A') kneeA.x -= lat;
      else kneeB.x += lat;
    }
  }

  const joints: Record<JointName, Joint> = {
    head,
    neck,
    chest,
    waist,
    pelvis,
    shoulderA,
    shoulderB,
    elbowA,
    elbowB,
    wristA,
    wristB,
    handA,
    handB,
    hipA,
    hipB,
    kneeA,
    kneeB,
    ankleA,
    ankleB,
  };
  pitch(joints, rig, square);
  hunch(joints, rig.hunch ?? 0, square, back);
  // A hand placed at the face by an idle (motions/idles.ts `anchor`): aimed at the head as it now stands (pitched,
  // hunched, any proportions), the elbow solved again; in front of the face, or behind the head seen from behind.
  const anchorA = idleKey?.anchor?.A;
  if (anchorA) {
    const h = joints.head;
    // Measured on the drawn figure (the grown body lifts the head relative to the hands): rows below the head joint.
    const dy = { brow: 4, mouth: 10, chin: 13, chest: 18 }[anchorA.to] + (anchorA.dy ?? 0);
    const w = {
      // Head-on, a hand at the chin or chest comes in from its own side (the jaw), so the face stays clear.
      x:
        h.x +
        (square ? -lat * (back ? 2 : anchorA.to === 'brow' || anchorA.to === 'mouth' ? 1 : 4) : 4) +
        (anchorA.dx ?? 0),
      y: h.y + dy,
      z: back ? h.z - 0.15 : h.z + 0.1,
    };
    // The arm on the far side (left-facing views) keeps its own depth behind the body.
    const farA = !square && left;
    if (farA) w.z = joints.shoulderA.z;
    const el = elbow(joints.shoulderA, w, 'A', false);
    joints.wristA = w;
    joints.elbowA = { ...el, z: back && !farA ? h.z - 0.15 : el.z };
    joints.handA = palm(joints.elbowA, w);
    if (back && !farA) joints.shoulderA = { ...joints.shoulderA, z: h.z - 0.15 };
    atFace.A = !back && !farA;
  }
  // A hand raised up to the head, or held up just in front of the chin, would draw its arm (and
  // blade) across the face; such an arm goes beside the head instead, drawn just behind it, so the
  // face always shows. It stays in front of the body.
  const byFace = (w: Joint) => w.y < head.y + 9 || (w.y < head.y + 16 && Math.abs(w.x - head.x) < 12);
  // Seen from straight behind, an arm reaching forward is hidden by the body (only the hand and
  // blade show past it), instead of lying flat across the back; seen from the front, so is an arm
  // swung or held behind the back.
  if (view === 'back' || view === 'front') {
    const behind = { A: LIMB[rig.arm]?.front === false, B: rig.far === 'back' };
    // (A hand resting on the hilt at the front of the hip, too: only its elbow shows, out at the side.)
    const hidden =
      view === 'back'
        ? { A: rig.arm !== 'hang' && !behind.A, B: rig.far === 'fwd' || st?.handB === 'hilt' }
        : behind;
    for (const s of ['A', 'B'] as const)
      if (hidden[s] && !byFace(joints[`wrist${s}`]))
        for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const) joints[`${n}${s}`].z = -0.2;
  }
  // Seen three-quarter from behind, the near arm reaching forward past its shoulder (a push, a guard, a hand on the
  // front of the hip) passes in front of the body, away from the camera: drawn under the torso (and the hair down
  // the back), its upper arm still showing past the body's edge (lane C0-POSE-FIX, rig/depthCheck.ts).
  if (view === 'back3') {
    const s = left ? 'B' : 'A';
    const w = joints[`wrist${s}`];
    const reaches = (s === 'A' ? rig.arm : rig.far) !== 'hang' || (s === 'B' && st?.handB === 'hilt');
    if (reaches && w.x > joints.chest.x + 2 && joints[`shoulder${s}`].z > 0)
      for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const) joints[`${n}${s}`].z = -0.2;
  }
  // ... but only a hand that is really beside or behind the head (cocked behind it, turned views face +x, or raised
  // above it): an arm crossing in front of the head stays in front, or the head shows through it when the body leans
  // (lane C0-POSE-FIX, rig/depthCheck.ts).
  // Seen from behind there is no face to keep clear, and an arm by the head is nearer the camera than the head.
  const besideHead = (w: Joint) => !back && ((!square && w.x < head.x - 2) || w.y < head.y - 5);
  for (const s of ['A', 'B'] as const)
    if (
      !atFace[s] &&
      byFace(joints[`wrist${s}`]) &&
      besideHead(joints[`wrist${s}`]) &&
      joints[`shoulder${s}`].z > head.z - 0.2
    )
      for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const) joints[`${n}${s}`].z = head.z - 0.15;
  // Resting hands on the side of the body away from the camera go behind it, arms and all: held
  // behind the back while facing the camera, or clasped in front while turned away.
  if ((resting === 'back' && !back) || (resting === 'front' && back))
    for (const s of restsA ? (['A', 'B'] as const) : (['B'] as const))
      for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const) joints[`${n}${s}`].z = -0.9;
  breathe(joints, pose, ph);
  if (st && stand?.inhale) lift(joints, st.breath);
  motion(joints, pose, square, walkNew || runNew ? walkTwist(rig) : 0);
  const moved = edit === undefined ? OVERRIDES.get(pose)?.views[view] : edit;
  if (moved) applyEdit(joints, moved, square);
  grow(joints, body, square);
  if (blink) rig.closed = true;
  // An in-between frame: the weapon arm part of the way to its next position.
  if (rig.armTo && rig.armT) {
    const to = skeleton(view8, pose, ph, lag, body, blink, edit, rig.armTo).joints;
    const t = rig.armT;
    const from = square ? null : { ...joints };
    for (const n of ['elbowA', 'wristA', 'handA'] as const)
      joints[n] = {
        x: joints[n].x + (to[n].x - joints[n].x) * t,
        y: joints[n].y + (to[n].y - joints[n].y) * t,
        z: t < 0.5 ? joints[n].z : to[n].z,
      };
    // Turned, the arm swings round the shoulder at its full length rather than folding short.
    if (from) swingArm(joints, from, to, 'A', t);
  }
  return {
    view,
    ...(left && { left }),
    pose,
    joints,
    toe: square ? { x: 0, y: 1 } : { x: 1, y: 0 },
    square,
    back,
    rig,
    phase: ph,
    shape,
    resting,
    foreA,
    ...(idleKey?.tool ? { tool: idleKey.tool } : {}),
  };
}

/** What moves with each joint when it is dragged: the body above it, or the rest of the limb. */
const CARRIES: Partial<Record<JointName, readonly JointName[]>> = (() => {
  const arm = (s: Side) => [`shoulder${s}`, `elbow${s}`, `wrist${s}`, `hand${s}`] as JointName[];
  const upper: JointName[] = ['chest', 'neck', 'head', ...arm('A'), ...arm('B')];
  return {
    pelvis: ['waist', ...upper, 'hipA', 'hipB'],
    waist: upper,
    chest: upper.slice(1),
    neck: ['head'],
    shoulderA: arm('A').slice(1),
    shoulderB: arm('B').slice(1),
    elbowA: ['wristA', 'handA'],
    elbowB: ['wristB', 'handB'],
    wristA: ['handA'],
    wristB: ['handB'],
  };
})();

/**
 * Moves joints by a hand-set delta. A moved joint carries what hangs off it (the waist carries the
 * whole upper body, an elbow the forearm and hand); then each arm and leg whose ends moved is bent
 * again so its bones keep their length — the elbow and knee follow unless they were set themselves,
 * and a hand keeps pointing along its forearm.
 */
export function applyEdit(j: Record<JointName, Joint>, d: JointDelta, square: boolean): void {
  const shift = new Map<JointName, [number, number]>();
  for (const [name, [dx, dy]] of Object.entries(d) as [JointName, readonly [number, number]][]) {
    if (!dx && !dy) continue;
    for (const n of [name, ...(CARRIES[name] ?? [])]) {
      const s = shift.get(n) ?? [0, 0];
      shift.set(n, [s[0] + dx, s[1] + dy]);
    }
  }
  if (!shift.size) return;
  for (const [n, [dx, dy]] of shift) {
    j[n].x += dx;
    j[n].y += dy;
  }
  const own = (n: JointName) => !!d[n] && (d[n]![0] !== 0 || d[n]![1] !== 0);
  /** The middle joint, bent again between two moved ends, toward the side it already bent to. */
  const rebend = (a: JointName, mid: JointName, c: JointName, l1: number, l2: number, fallback: Pt2) => {
    if (own(mid) || (!shift.has(a) && !shift.has(c))) return;
    // A hand or foot set beyond the limb's reach is pulled back to it.
    const A = j[a];
    const C = j[c];
    const dist = Math.hypot(C.x - A.x, C.y - A.y);
    const max = (l1 + l2) * 0.98;
    if (dist > max) {
      C.x = A.x + ((C.x - A.x) / dist) * max;
      C.y = A.y + ((C.y - A.y) / dist) * max;
    }
    const M = j[mid];
    const off = { x: M.x - (A.x + C.x) / 2, y: M.y - (A.y + C.y) / 2 };
    const pref = Math.hypot(off.x, off.y) > 0.3 ? off : fallback;
    const b = bend(A, C, l1, l2, pref);
    M.x = b.x;
    M.y = b.y;
  };
  for (const s of ['A', 'B'] as Side[]) {
    rebend(
      `shoulder${s}`,
      `elbow${s}`,
      `wrist${s}`,
      BODY.upperArm,
      BODY.forearm,
      square ? { x: s === 'A' ? 1 : -1, y: 0.4 } : { x: -0.6, y: 0.8 },
    );
    if (!own(`hand${s}`) && (shift.has(`wrist${s}`) || shift.has(`elbow${s}`))) {
      const el = j[`elbow${s}`];
      const wr = j[`wrist${s}`];
      const len = Math.max(0.001, Math.hypot(wr.x - el.x, wr.y - el.y));
      j[`hand${s}`].x = wr.x + ((wr.x - el.x) / len) * BODY.hand;
      j[`hand${s}`].y = wr.y + ((wr.y - el.y) / len) * BODY.hand;
    }
    rebend(`hip${s}`, `knee${s}`, `ankle${s}`, BODY.thigh, BODY.shin, { x: 1, y: 0 });
  }
}

type Pt2 = { x: number; y: number };

/** How much longer the grown body is than the classic figure's, from the soles to the neck. */
const GROWN = 1.15;

/**
 * Age and height, applied to a finished pose: the body is squashed or stretched upward from the
 * soles while the head keeps its size (so a child's head is large for its body), and an elder
 * stoops — head, neck and shoulders carried forward and down, the arms hanging from them.
 */
function grow(j: Record<JointName, Joint>, body: BodyShape, square: boolean): void {
  const k = AGES[body.age ?? 'adult'].scale * HEIGHTS[body.height ?? 'average'].scale;
  const neckGap = j.neck.y - j.head.y;
  if (k !== 1) for (const p of Object.values(j)) p.y = GROUND - (GROUND - p.y) * k;
  if (body.grown) {
    // Stretched up from the soles to the neck; the head rides on the neck at its own size.
    // Whole rows, so a step's bob moves the whole body by the same pixels (a fraction would round
    // differently for each part and set the figure trembling as it walks).
    for (const p of Object.values(j)) p.y = Math.round(GROUND - (GROUND - p.y) * GROWN);
    // The head comes down two rows: the stretch would otherwise lengthen the neck.
    j.head.y = j.neck.y - neckGap + 2;
  }
  if (body.age !== 'elder') return;
  const ahead = square ? 0 : 1;
  const move = (names: JointName[], dx: number, dy: number) => {
    for (const n of names) {
      j[n].x += dx * ahead;
      j[n].y += dy;
    }
  };
  move(['head'], 2.5, 2);
  move(['neck'], 1.5, 1);
  move(['chest'], 1, 0.5);
  for (const s of ['A', 'B'] as Side[]) move([`shoulder${s}`, `elbow${s}`, `wrist${s}`, `hand${s}`], 1, 0.5);
}

/**
 * How far each pose turns the shoulders about the spine (positive: the right shoulder forward),
 * and how it carries the head (forward for a strike, thrown back by a hit). The classic poses only
 * moved arms and legs; these make a cut swing from the waist.
 */
const TWIST: Partial<Record<Pose, number>> = {
  windup: -3,
  atk: 2,
  cut: 3,
  follow: 2,
  lowGuard: -1,
  thrust: 3,
  overhead: -2,
  blockHit: -1,
  dash: 1,
};

/** How far the chest rises with each breath standing at ease and in the fighting stance. */
const BREATH: Partial<Record<Pose, number>> = { idle: 0.5, ready: 0.75 };

/**
 * Breathing: the chest, shoulders, arms and head rise and settle once a cycle, smoothly, while the
 * hips and legs stay still, so the figure is alive without bobbing as if struck. Frame 0 is the
 * breath out (the pose's own placing), so a motion that starts from a key is unchanged.
 */
function breathe(j: Joints, pose: Pose, ph: number): void {
  const amp = BREATH[pose];
  if (amp) lift(j, amp * 0.5 * (1 - Math.cos(((ph % FRAMES) / FRAMES) * Math.PI * 2)));
}

/** The chest, shoulders, arms and head raised by a breath of `by` pixels, the hips and legs still. */
function lift(j: Joints, by: number): void {
  for (const n of ['chest', 'neck', 'head'] as const) j[n].y -= by;
  for (const s of ['A', 'B'] as const)
    for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const) j[`${n}${s}`].y -= by;
  // The waist between moves half as far, so the belly does not slide over the belt.
  j.waist.y -= by * 0.5;
}
const NOD: Partial<Record<Pose, [number, number]>> = {
  atk: [1, 0.5],
  cut: [1, 0.5],
  thrust: [1, 0.5],
  dash: [1, 0.5],
  hurt: [-1, -1],
  blockHit: [-1, -0.5],
  cast: [0, -0.5],
  bow: [1, 1.5],
  mend: [1, 1],
  cower: [-1, 1],
};

/** Arms set against the body (on the hips, folded, clasped, at the face): they pitch with the torso. */
const WITH_BODY: ReadonlySet<string> = new Set([
  'hip',
  'fold',
  'chin',
  'face',
  'clap',
  'clapOpen',
  'overBack',
  'clasp',
  'chest',
  'sit',
]);

/**
 * Bends the body at the hips by the pose's `tilt`: head, spine and shoulders turn about the pelvis,
 * so a bow folds forward and a recoil arches back instead of the whole torso sliding upright. An
 * arm set against the body turns with it; a reaching or hanging arm keeps its own direction (a
 * levelled blade stays level, a loose arm still hangs) and only moves with its shoulder. Head-on
 * the bend is toward or away from the camera, which only shortens the torso on screen.
 */
function pitch(j: Record<JointName, Joint>, rig: Rig, square: boolean): void {
  if (!rig.tilt) return;
  const a = (rig.tilt * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const p = j.pelvis;
  const turn = (q: Joint) => {
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    if (square) q.y = p.y + dy * cos;
    else {
      q.x = p.x + dx * cos - dy * sin;
      q.y = p.y + dx * sin + dy * cos;
    }
  };
  for (const n of ['head', 'neck', 'chest', 'waist'] as const) turn(j[n]);
  for (const [s, arm] of [
    ['A', rig.arm],
    ['B', rig.far],
  ] as const) {
    const sh = j[`shoulder${s}`];
    const [x0, y0] = [sh.x, sh.y];
    turn(sh);
    const rigid = WITH_BODY.has(arm) && !square;
    for (const n of [`elbow${s}`, `wrist${s}`, `hand${s}`] as const) {
      if (rigid) turn(j[n]);
      else {
        j[n].x += sh.x - x0;
        j[n].y += sh.y - y0;
      }
    }
  }
}

/**
 * A rounded upper back (an elder's stoop; `Rig.hunch`, design px): the chest, neck and head carried forward and down
 * over hips that stay upright, the head most, the shoulders and arms with the chest. Turned, forward is +x; head-on,
 * the head drops (toward the camera from the front, away from it behind).
 */
function hunch(j: Record<JointName, Joint>, h: number, square: boolean, back: boolean): void {
  if (!h) return;
  const move = (q: Joint, fx: number, fy: number) => {
    q.x += square ? 0 : h * fx;
    q.y += h * fy * (square && back ? 0.6 : 1);
  };
  move(j.chest, 0.35, 0.15);
  move(j.neck, 0.8, 0.45);
  move(j.head, 1.1, 0.7);
  for (const s of ['A', 'B'] as const) {
    const sh = j[`shoulder${s}`];
    const [x0, y0] = [sh.x, sh.y];
    move(sh, 0.5, 0.3);
    for (const n of [`elbow${s}`, `wrist${s}`, `hand${s}`] as const) {
      j[n].x += sh.x - x0;
      j[n].y += sh.y - y0;
    }
  }
}

type Joints = Record<JointName, Joint>;

/**
 * Which way the weapon hand turns round its shoulder from key `A` to key `B` (+1 clockwise on
 * screen, −1 counter-clockwise, 0 head-on or barely moving), for the blade to follow it.
 */
function handWay(A: Skeleton, B: Skeleton): number {
  if (A.square) return 0;
  const ang = (k: Skeleton) =>
    Math.atan2(k.joints.wristA.y - k.joints.shoulderA.y, k.joints.wristA.x - k.joints.shoulderA.x);
  const d = Math.atan2(Math.sin(ang(B) - ang(A)), Math.cos(ang(B) - ang(A)));
  return Math.abs(d) < 0.3 ? 0 : Math.sign(d);
}

/**
 * `p` (about `ca`, in one key) and `q` (about `cb`, in the next) blended as a turn about a centre
 * rather than a straight line: the angle turns the short way round, the distance changes evenly,
 * landing about `c`. A limb swung this way keeps its length all the way through, and a blade arm
 * sweeps round the shoulder in an arc instead of cutting across the chord and folding short.
 */
function turned(ca: Pt2, p: Pt2, cb: Pt2, q: Pt2, c: Pt2, t: number): Pt2 {
  const a0 = Math.atan2(p.y - ca.y, p.x - ca.x);
  const a1 = Math.atan2(q.y - cb.y, q.x - cb.x);
  const da = Math.atan2(Math.sin(a1 - a0), Math.cos(a1 - a0));
  const r0 = Math.hypot(p.x - ca.x, p.y - ca.y);
  const r1 = Math.hypot(q.x - cb.x, q.y - cb.y);
  const a = a0 + da * t;
  const r = r0 + (r1 - r0) * t;
  return { x: c.x + Math.cos(a) * r, y: c.y + Math.sin(a) * r };
}

/**
 * One arm of an in-between frame (turned views): the wrist swung round the shoulder from where one
 * key holds it to where the next does (kept off the face if both keys keep it off), the elbow bent
 * again at full bone length toward the side the blend puts it, the palm along the forearm.
 */
function swingArm(j: Joints, A: Joints, B: Joints, s: Side, t: number): void {
  const sh = j[`shoulder${s}`];
  const w = turned(A[`shoulder${s}`], A[`wrist${s}`], B[`shoulder${s}`], B[`wrist${s}`], sh, t);
  // As far off the head as the keys keep it (up to the usual clearance): a blade brought over
  // from behind the head passes above it, not through the face.
  const off0 = (k: Joints) =>
    Math.min(HEAD_CLEAR, Math.hypot(k[`wrist${s}`].x - k.head.x, k[`wrist${s}`].y - k.head.y));
  const need = off0(A) + (off0(B) - off0(A)) * t;
  const hd = Math.hypot(w.x - j.head.x, w.y - j.head.y);
  if (hd < need && hd > 0.001) {
    w.x = j.head.x + ((w.x - j.head.x) / hd) * need;
    w.y = j.head.y + ((w.y - j.head.y) / hd) * need;
  }
  // The arm's bones as long as the keys draw them (a grown body's are longer than `BODY`'s).
  const upper = boneBetween(A, B, t, `shoulder${s}`, `elbow${s}`);
  const fore = boneBetween(A, B, t, `elbow${s}`, `wrist${s}`);
  const max = (upper + fore) * 0.98;
  const d = Math.hypot(w.x - sh.x, w.y - sh.y);
  if (d > max) {
    w.x = sh.x + ((w.x - sh.x) / d) * max;
    w.y = sh.y + ((w.y - sh.y) / d) * max;
  }
  const el = j[`elbow${s}`];
  const off = { x: el.x - (sh.x + w.x) / 2, y: el.y - (sh.y + w.y) / 2 };
  const ka = A[`elbow${s}`];
  const keyOff = {
    x: ka.x - (A[`shoulder${s}`].x + A[`wrist${s}`].x) / 2,
    y: ka.y - (A[`shoulder${s}`].y + A[`wrist${s}`].y) / 2,
  };
  const e = bend(sh, w, upper, fore, Math.hypot(off.x, off.y) > 0.3 ? off : keyOff);
  j[`elbow${s}`] = { ...el, x: e.x, y: e.y };
  const wz = j[`wrist${s}`].z;
  j[`wrist${s}`] = { x: w.x, y: w.y, z: wz };
  const fl = Math.max(0.001, Math.hypot(w.x - e.x, w.y - e.y));
  j[`hand${s}`] = {
    ...j[`hand${s}`],
    x: w.x + ((w.x - e.x) / fl) * BODY.hand,
    y: w.y + ((w.y - e.y) / fl) * BODY.hand,
  };
}

/**
 * Fixes up a motion frame blended between two key skeletons (turned views): joints moved in
 * straight lines shorten every bone that turns, so the spine and shoulders are turned about the
 * pelvis, each arm swung round its shoulder and each leg bent again at full length. A foot that
 * moves along the ground between the keys is picked up and set down (a step) instead of skating.
 */
function between(j: Joints, A: Joints, B: Joints, t: number): void {
  const p = j.pelvis;
  for (const n of ['waist', 'chest', 'neck', 'head', 'shoulderA', 'shoulderB'] as const) {
    const q = turned(A.pelvis, A[n], B.pelvis, B[n], p, t);
    j[n] = { ...j[n], x: q.x, y: q.y };
  }
  swingArm(j, A, B, 'A', t);
  swingArm(j, A, B, 'B', t);
  for (const s of ['A', 'B'] as const) {
    // The leg's bones as long as the keys draw them: a grown body's legs are longer than `BODY`'s,
    // and solved at `BODY`'s length they would seem out of reach and lift every foot in between.
    const thigh = boneBetween(A, B, t, `hip${s}`, `knee${s}`);
    const shin = boneBetween(A, B, t, `knee${s}`, `ankle${s}`);
    const L = thigh + shin;
    const ankle = j[`ankle${s}`];
    const dx = B[`ankle${s}`].x - A[`ankle${s}`].x;
    const grounded = Math.max(A[`ankle${s}`].y, B[`ankle${s}`].y) >= GROUND - 3.5;
    if (grounded && Math.abs(dx) >= 2) ankle.y -= Math.min(4, Math.abs(dx) * 0.6) * Math.sin(Math.PI * t);
    const hip = j[`hip${s}`];
    const hx = ankle.x - hip.x;
    // Out of the straight leg's reach (the back foot of a lunge), the heel rises as on a key.
    if (Math.hypot(hx, ankle.y - hip.y) > L + 0.6 && Math.abs(hx) < L)
      ankle.y = hip.y + Math.sqrt(L * L - hx * hx);
    const k = bend(hip, ankle, thigh, shin, { x: 1, y: 0 });
    j[`knee${s}`] = { ...j[`knee${s}`], x: k.x, y: k.y };
  }
}

/** Spline blending of motion keys (motions/spline.ts); off: the old eased straight blend between two keys. */
const SPLINE = true;
/** Most a tangent may carry a joint off the straight blend (design px): no wild overshoot between far-apart keys. */
const CURVE_MAX = 5;

/**
 * Bends an in-between frame (blended from `A` to `B` at `t`, arcs and all) onto the Catmull-Rom curve through the
 * keys `P`, `A`, `B`, `N`: each joint moved by the Hermite tangent terms (`m0`, `m1` the basis weights), then the
 * elbows and knees bent again at their keys' bone lengths so no limb stretches. The palm keeps to the forearm.
 */
function curve(
  j: Joints,
  P: Joints,
  A: Joints,
  B: Joints,
  N: Joints,
  m0: number,
  m1: number,
  t: number,
): void {
  const moved = new Set<JointName>();
  for (const n of Object.keys(j) as JointName[]) {
    let dx = ((B[n].x - P[n].x) / 2) * m0 + ((N[n].x - A[n].x) / 2) * m1;
    let dy = ((B[n].y - P[n].y) / 2) * m0 + ((N[n].y - A[n].y) / 2) * m1;
    if (Math.abs(dx) + Math.abs(dy) < 0.05) continue;
    const d = Math.hypot(dx, dy);
    if (d > CURVE_MAX) {
      dx *= CURVE_MAX / d;
      dy *= CURVE_MAX / d;
    }
    moved.add(n);
    j[n] = { ...j[n], x: j[n].x + dx, y: j[n].y + dy };
  }
  // Keys that hold still either side (a breath, a planted stance): the straight blend stands.
  if (!moved.size) return;
  for (const s of ['A', 'B'] as const) {
    for (const [root, mid, end] of [
      [`shoulder${s}`, `elbow${s}`, `wrist${s}`],
      [`hip${s}`, `knee${s}`, `ankle${s}`],
    ] as const) {
      if (!moved.has(root) && !moved.has(mid) && !moved.has(end)) continue;
      const l1 = boneBetween(A, B, t, root, mid);
      const l2 = boneBetween(A, B, t, mid, end);
      const r = j[root];
      const e = j[end];
      // Never past the limb's length (the curve may carry a hand a little farther than either key).
      const dd = Math.hypot(e.x - r.x, e.y - r.y);
      const max = l1 + l2;
      if (dd > max) j[end] = { ...e, x: r.x + ((e.x - r.x) / dd) * max, y: r.y + ((e.y - r.y) / dd) * max };
      const m = j[mid];
      const pref = { x: m.x - (r.x + j[end].x) / 2, y: m.y - (r.y + j[end].y) / 2 };
      const ka = A[mid];
      const keyPref = { x: ka.x - (A[root].x + A[end].x) / 2, y: ka.y - (A[root].y + A[end].y) / 2 };
      const q = bend(r, j[end], l1, l2, Math.hypot(pref.x, pref.y) > 0.3 ? pref : keyPref);
      j[mid] = { ...m, x: q.x, y: q.y };
    }
    // The palm keeps its own turn at the wrist (a grip may break the line), as long as the keys draw it.
    if (!moved.has(`wrist${s}`) && !moved.has(`hand${s}`)) continue;
    const w = j[`wrist${s}`];
    const hl = boneBetween(A, B, t, `wrist${s}`, `hand${s}`);
    const hx = j[`hand${s}`].x - w.x;
    const hy = j[`hand${s}`].y - w.y;
    const hd = Math.hypot(hx, hy);
    if (hd > 0.001) j[`hand${s}`] = { ...j[`hand${s}`], x: w.x + (hx / hd) * hl, y: w.y + (hy / hd) * hl };
  }
}

/** A bone's length `t` of the way from key `A` to key `B`. */
function boneBetween(A: Joints, B: Joints, t: number, a: JointName, b: JointName): number {
  const len = (k: Joints) => Math.hypot(k[b].x - k[a].x, k[b].y - k[a].y);
  return len(A) + (len(B) - len(A)) * t;
}

function motion(j: Record<JointName, Joint>, pose: Pose, square: boolean, extraTwist = 0): void {
  const t = (TWIST[pose] ?? (STANCE_KEYS.get(pose) ?? IDLE_KEYS.get(pose))?.stance.turn ?? 0) + extraTwist;
  // Head-on a twist is a turn toward the camera, which hardly moves anything sideways.
  if (t && !square)
    for (const [s, sign] of [
      ['A', 1],
      ['B', -1],
    ] as const)
      for (const n of [`shoulder${s}`, `elbow${s}`, `wrist${s}`, `hand${s}`] as const)
        j[n].x += t * sign * 0.7;
  const nod = NOD[pose];
  if (nod) {
    if (!square) j.head.x += nod[0];
    j.head.y += nod[1];
  }
}
